import apiClient from './client';
import type { ApiErrorResponse } from './auth';

export interface UserProfile {
  user_id: number;
  user_email: string;
  user_name: string;
  user_phone: string | null;
  user_logo: string | null;
  email_verified_at: string | null;
}

export interface UpdateUserProfileRequest {
  user_name?: string;
  user_phone?: string | null;
  user_password?: string;
}

/**
 * Get user profile
 */
/**
 * Wire shape of the profile endpoints. The API answers in camelCase, and PUT
 * wraps the payload in the standard { message_key, data } envelope while GET
 * returns it flat — the previous `as UserProfile` casts hid both mismatches, so
 * a saved name never reached the UI (it silently fell back to the stale auth
 * value, which is why SET-006 only passed while leftover test data happened to
 * match the expected name).
 */
interface UserProfileWire {
  userId: number;
  userEmail: string;
  userName: string;
  userPhone: string | null;
  userLogo?: string | null;
  emailVerifiedAt?: string | null;
}

const toUserProfile = (wire: UserProfileWire): UserProfile => ({
  user_id: wire.userId,
  user_email: wire.userEmail,
  user_name: wire.userName,
  user_phone: wire.userPhone ?? null,
  user_logo: wire.userLogo ?? null,
  email_verified_at: wire.emailVerifiedAt ?? null,
});

/** PUT answers { message_key, data: {...} }; GET answers the object directly. */
const unwrap = (payload: UserProfileWire | { data: UserProfileWire }): UserProfileWire =>
  'data' in payload ? payload.data : payload;

export async function getUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get<UserProfileWire | { data: UserProfileWire } | ApiErrorResponse>('/user/profile');
  const data = response.data;

  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to get user profile');
  }

  return toUserProfile(unwrap(data));
}

/**
 * Update user profile
 */
export async function updateUserProfile(payload: UpdateUserProfileRequest): Promise<UserProfile> {
  const response = await apiClient.put<UserProfileWire | { data: UserProfileWire } | ApiErrorResponse>('/user/profile', payload);
  const data = response.data;

  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to update user profile');
  }

  return toUserProfile(unwrap(data));
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
  new_password_confirmation: string;
}

/**
 * Change user password
 */
export async function changePassword(payload: ChangePasswordRequest): Promise<void> {
  const response = await apiClient.post<{ message: string } | ApiErrorResponse>('/user/password', payload);
  const data = response.data;
  
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to change password');
  }
}

export interface DeleteAccountRequest {
  password: string;
}

/**
 * Delete user account (soft delete)
 */
export async function deleteAccount(payload: DeleteAccountRequest): Promise<void> {
  const response = await apiClient.delete<{ message: string } | ApiErrorResponse>('/user/account', { data: payload });
  const data = response.data;
  
  if ('error' in data) {
    throw new Error(data.error?.message || 'Failed to delete account');
  }
}

